import { cn } from "@newxsystems/shared";
import type { ITootBadgeListProps, TootBadgeKind, TootBadgeTone } from "./TootBadgeList.props";

const toneClasses: Record<TootBadgeTone, string> = {
  accent:
    "border-orange-400/25 bg-orange-400/10 font-semibold uppercase tracking-[0.18em] text-toot-orange",
  danger: "border-red-500/60 bg-red-500/10 font-semibold text-primary-700",
  muted: "border-toot-grid/90 bg-toot-surface-raised font-medium text-toot-muted",
  neutral: "border-toot-grid bg-card font-semibold text-toot-foreground",
  success: "border-emerald-500/60 bg-emerald-500/10 font-semibold text-emerald-700",
  warning: "border-amber-500/60 bg-amber-500/10 font-semibold text-amber-700",
};

const kindTone: Record<TootBadgeKind, TootBadgeTone> = {
  badge: "neutral",
  category: "accent",
  metadata: "muted",
  status: "success",
  tag: "warning",
};

export const TootBadgeList = ({
  ariaLabel,
  className,
  items,
  tone = "muted",
  withAccessibleLabels = false,
}: ITootBadgeListProps) => {
  if (!items?.length) {
    return null;
  }

  if (withAccessibleLabels) {
    return (
      <dl aria-label={ariaLabel} className={cn("flex flex-wrap gap-2", className)}>
        {items.map((item, index) => {
          const itemTone = item.tone ?? (item.kind ? kindTone[item.kind] : tone);

          return (
            <div
              className={cn("rounded-full border px-3 py-1 text-xs", toneClasses[itemTone])}
              key={`${item.label ?? "value"}-${item.value}-${index}`}
            >
              {item.label ? <dt className="sr-only">{item.label}</dt> : null}
              <dd>{item.value}</dd>
            </div>
          );
        })}
      </dl>
    );
  }

  return (
    <ul aria-label={ariaLabel} className={cn("toot-badge-list flex flex-wrap gap-2", className)}>
      {items.map((item, index) => {
        const itemTone = item.tone ?? (item.kind ? kindTone[item.kind] : tone);

        return (
          <li
            className={cn("rounded-full border px-3 py-1 text-xs", toneClasses[itemTone])}
            key={`${item.label ?? "value"}-${item.value}-${index}`}
          >
            {item.value}
          </li>
        );
      })}
    </ul>
  );
};

TootBadgeList.displayName = "Content::TootBadgeList";
